import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Search, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";

// Plattform-Admin-Übersicht: Wholesale-Partner-Toggle je Agentur.
// Sichtbar nur für role === "platform_admin".
export default function PlatformAdminAgencies() {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/agencies");
      setAgencies(r.data.agencies || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Agenturen konnten nicht geladen werden";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "platform_admin") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggle = async (agencyId, next) => {
    try {
      await api.patch(`/admin/agencies/${agencyId}/wholesale-partner`, { is_wholesale_partner: next });
      setAgencies((prev) => prev.map((a) => (a.agency_id === agencyId ? { ...a, is_wholesale_partner: next } : a)));
      toast.success(next ? "Wholesale-Partner aktiviert" : "Wholesale-Partner deaktiviert");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Konnte nicht gespeichert werden";
      toast.error(detail);
    }
  };

  if (user && user.role !== "platform_admin") {
    return (
      <Card className="p-8 max-w-md mx-auto" data-testid="admin-access-denied">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-900">Zugriff verweigert</p>
            <p className="text-sm text-slate-600 mt-1">Diese Seite ist nur für Platform-Admins.</p>
          </div>
        </div>
      </Card>
    );
  }

  const filtered = agencies.filter((a) => {
    if (!search.trim()) return true;
    const t = `${a.name} ${a.email} ${a.agency_slug}`.toLowerCase();
    return t.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="platform-admin-agencies-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7 text-orange-600" />
        <div>
          <h1
            className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Platform-Admin · Agenturen
          </h1>
          <p className="mt-1 text-slate-600">
            Schalten Sie Agenturen als Wholesale-Partner frei. Default ist deaktiviert.
          </p>
        </div>
      </div>

      <Card className="border-slate-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="admin-search"
            placeholder="Suche nach Name, E-Mail, Slug …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr className="text-left">
                <th className="px-6 py-3 font-semibold">Agentur</th>
                <th className="px-6 py-3 font-semibold">E-Mail</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Erstellt</th>
                <th className="px-6 py-3 font-semibold text-right">Wholesale-Partner</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-500">
                    Wird geladen …
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-500">
                    Keine Agenturen gefunden.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a.agency_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-slate-900">{a.name || "—"}</div>
                      <div className="text-xs text-slate-500">{a.agency_slug || a.agency_id}</div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-700">{a.email || "—"}</td>
                    <td className="px-6 py-3 text-sm text-slate-700">{a.status || "active"}</td>
                    <td className="px-6 py-3 text-sm text-slate-500">{formatDate(a.created_at)}</td>
                    <td className="px-6 py-3 text-right">
                      <Switch
                        data-testid={`wholesale-toggle-${a.agency_id}`}
                        checked={!!a.is_wholesale_partner}
                        onCheckedChange={(v) => toggle(a.agency_id, !!v)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
